import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'next/router';
import {
  compose, Query, graphql, withApollo,
} from 'react-apollo';
import { util } from 'node-forge';
import * as Portals from 'react-reverse-portal';

import withReversePortal from '../hocs/withReversePortal';
import withAppData from '../hocs/withAppData';

import { initSentry } from '../lib/sentry';
import withData from '../lib/withData';
import { dataLayerManager } from '../lib/dataLayer';
import { virtualPageViewTypes } from '../lib/constants';
import {
  getDefaultOutfit,
  generateDefaultPartyMembers,
} from '../lib/helpers/quote';
import isLoggedIn from '../lib/isLoggedIn';
import { getMembershipDialogProps } from '../lib/helpers/dialogMembership';

import AppWrapper from '../components/Layouts/AppWrapper';
import Quote from '../components/Quote';
import DrawerSearch from '../components/Layouts/Drawer';
import SearchCrossings, { decodeAndParseQuery } from '../components/Search/SearchCrossings';
import SearchWrapper from '../components/Layouts/SearchWrapper';
import MembershipDialog from '../components/Dialog/MembershipDialog';
import { LoadingContainer } from '../components/Layouts/AppWrapper/index.style';
import LoadingSpinner from '../components/ui/Loading/LoadingSpinner';
import { MapCrossings } from '../components/Map';

import GET_QUOTE from '../components/Quote/graphql/getQuote';
import GET_CROSSINGS_DATA from '../components/Search/graphql/getCrossingsData';
import RESET_SESSION from '../components/Quote/graphql/resetSession';
import FetchPolicy from '../constants/FetchPolicy';

import IbePropTypes from '../IbePropTypes';
import { DIALOG_MEMBERSHIP_TYPE } from '../config/dialogMembership';
import { BASKET_STATES } from '../config/products';
import routes from '../constants/routes';
import { FEATURE_FLAGS, getFeatureFlag } from '../config/featureFlags';
import withCrossingsConfig from '../hocs/withCrossingsConfig';

initSentry();

class Crossings extends Component {
  static propTypes = {
    client: PropTypes.shape(IbePropTypes.client).isRequired,
    quote: PropTypes.shape(IbePropTypes.quote),
    handleFiltersClick: PropTypes.func,
    handleResultClick: PropTypes.func,
    isMap: PropTypes.bool,
    toggle: PropTypes.bool,
    toggleMap: PropTypes.func,
    env: PropTypes.string,
    router: PropTypes.shape(IbePropTypes.router),
    resetSession: PropTypes.func.isRequired,
    appData: PropTypes.shape({
      configuration: PropTypes.shape(IbePropTypes.configuration),
    }),
    crossingsConfig: PropTypes.shape(IbePropTypes.crossingsConfig).isRequired,
    portalNode: IbePropTypes.portalNode.isRequired,
  };

  static defaultProps = {
    handleFiltersClick() { },
    handleResultClick() { },
    isMap: false,
    toggle: true,
    toggleMap() { },
    env: 'local',
    router: {
      query: {},
    },
    appData: {},
    quote: undefined,
  };

  constructor(props) {
    super(props);
    this.state = {
      dialog: null,
    };
  }

  componentDidMount() {
    this.checkToPushVirtualPageView();
  }

  componentDidUpdate() {
    this.checkToPushVirtualPageView();
  }

  proceedToCheckout = () => {
    this.setState({
      dialog: null,
    });

    if (!isLoggedIn()) {
      const { client } = this.props;
      // client.writeData({ data: { loginPopUp: true } });
      client.writeData({
        data: {
          loginPopUp: {
            __typename: 'LoginPopUp',
            open: true,
            title: '',
            prompt: '',
            redirectTo: '',
          },
        },
      });
      return;
    }

    this.props.router.push({
      pathname: routes.checkout,
    });
  }

  onEmptyBasket = async (cb) => {
    this.props.resetSession();
    this.onCloseDialog();

    if (cb) {
      await cb();
    }
  }

  onConfirmEmptyBasket = (cb) => {
    const dialogType =
      DIALOG_MEMBERSHIP_TYPE.PRODUCT_CONFIRM_EMPTY_BASKET;

    const newDialog =
      getMembershipDialogProps({
        dialogType,
      });

    this.setState({
      dialog: {
        ...newDialog,
        primaryAction: () => this.onEmptyBasket(cb),
        secondaryAction: this.onCloseDialog,
      },
    });
  }

  onCloseDialog = () => {
    this.setState({
      dialog: null,
    });
  }

  onProductTypeMismatch = (quote) => (isOverseasProduct, cb) => {
    if (!quote?.basketState) {
      return false;
    }

    const isBasketOverseas = quote.basketState === BASKET_STATES.OVERSEAS;

    if ((isOverseasProduct && !isBasketOverseas) ||
      (!isOverseasProduct && isBasketOverseas)) {
      const dialogType = isBasketOverseas ?
        DIALOG_MEMBERSHIP_TYPE.PRODUCT_TYPE_MISMATCH_OVERSEAS :
        DIALOG_MEMBERSHIP_TYPE.PRODUCT_TYPE_MISMATCH_UK;

      const newDialog =
        getMembershipDialogProps({
          dialogType,
        });
      this.setState({
        dialog: {
          ...newDialog,
          primaryAction: this.proceedToCheckout,
          secondaryAction: this.onCloseDialog,
          handleFooterAction: () => this.onConfirmEmptyBasket(cb),
        },
      });

      return true;
    }

    return false;
  }

  onBasketToggle = (isOpen, getBasketOpenStateFunc) => {
    // retain the supplied func for subsequent state checks rather than save the
    // state in this instance.
    this.getBasketOpenState = getBasketOpenStateFunc;

    if (isOpen) {
      dataLayerManager.pushVirtualPageViewEvent(virtualPageViewTypes.BASKET);
    } else {
      this.checkToPushVirtualPageView();
    }
  }

  checkToPushVirtualPageView() {
    // Crossings
    let page = virtualPageViewTypes.CROSSINGS;

    // Crossings Results
    if (this.props.router.query.showResults) page = virtualPageViewTypes.CROSSINGS_RESULTS;

    // Crossings Details
    // TODO refactor to elegantly detect page view
    if (document.querySelector('[data-quote-crossing-page-view="true"]')) page = virtualPageViewTypes.CROSSING_DETAILS;

    if (this.isBasketOpen()) {
      page = virtualPageViewTypes.BASKET;
    }

    dataLayerManager.pushVirtualPageViewEvent(page);
  }

  /**
   * We use a function supplied by SearchWrapper to check its state.
   */
  isBasketOpen() {
    if (this.getBasketOpenState) {
      return this.getBasketOpenState();
    }

    return false;
  }

  render() {
    const combinedConfiguration = {
      ...this.props.appData.configuration,
      ...this.props.crossingsConfig,
    };

    if (!combinedConfiguration) {
      return (
        <LoadingContainer>
          <LoadingSpinner />
        </LoadingContainer>
      );
    }
    const parsedDefaultQuery = {
      /**
       * Should be true if sameReturnRoute is enabled and itinerary has a routeCodeWeb value,
       * or if no route is selected at all. If it is enabled then payload will use routeCodeWeb
       * as the crossingRouteCode instead of the routeCode.
       */
      allowAlternativeRoutes: false,

      inboundItinerary: {},
      outboundItinerary: {
        arrivalPortName: '',
        crossingDateTime: '',
        crossingRouteCode: '',
        departurePortName: '',
      },
      outfit: getDefaultOutfit(),
      partyMembers: generateDefaultPartyMembers(
        combinedConfiguration,
        true,
      ),

      /**
       * Enabled if same return route is enabled and route code web exists,
       * or no route is selected at all. Enabling this will try to automatically
       * assign same return route for inbound when changing an outbound port.
       */
      sameReturnRoute: true,
    };
    const { portalNode, router } = this.props;
    const { query } = router;
    const defaultQuery = {
      search: util.encode64(JSON.stringify(parsedDefaultQuery)),
      bookingWidget: query.bookingWidget,
    };

    const expand = !!(query.showResults && query.search && query.bookingWidget !== 'true');
    const allowFeatureFlagMap = getFeatureFlag(FEATURE_FLAGS.MAP);

    return (
      <AppWrapper>
        <SearchWrapper
          defaultQuery={defaultQuery}
          onBasketToggle={this.onBasketToggle}
          renderMap={mapProps => allowFeatureFlagMap && portalNode && (
            <Portals.OutPortal
              node={portalNode}
              {...mapProps}
              query={decodeAndParseQuery(mapProps.query)}
            />
          )}
          crossings
        >
          {searchWrapperProps => (
            <DrawerSearch
              expand={expand}
              hideHandle={searchWrapperProps.showResults}
              isMap={searchWrapperProps.isMap}
              stickyAdvert={!searchWrapperProps.showResults}
              toggle={searchWrapperProps.toggle}
              toggleBasket={searchWrapperProps.toggleBasket}
              toggleMap={searchWrapperProps.toggleMap}
              isBasketVisible={searchWrapperProps.isBasketVisible}
              searchTabsClick={searchWrapperProps.handleResultClear}
              onProductTypeMismatch={this.onProductTypeMismatch(this.props.quote)}
              show
            >
              {drawerSearchProps => (
                <Quote isOverseas query={query}>
                  {({
                    quote, countBookings, onlyExtras,
                  }) => (
                    <Query
                      query={GET_CROSSINGS_DATA}
                    >
                      {({ data, loading }) => !loading && (
                        <SearchCrossings
                          {...searchWrapperProps}
                          // TODO refactor this, quick solution to prompt page view change
                          onResultClick={
                            (...args) => {
                              searchWrapperProps.onResultClick(...args);
                              dataLayerManager.pushVirtualPageViewEvent(
                                virtualPageViewTypes.CROSSING_DETAILS,
                              );
                            }
                          }
                          defaultMaxCrossingAccomQuantity={
                            combinedConfiguration?.defaultMaxCrossingAccomQuantity
                          }
                          quote={quote}
                          countBookings={countBookings}
                          onlyExtras={onlyExtras}
                          formRef={drawerSearchProps.formRef}
                          height={drawerSearchProps.height}
                          ports={combinedConfiguration?.ports}
                          routes={combinedConfiguration?.routes}
                          onProductTypeMismatch={this.onProductTypeMismatch}
                        />
                      )}
                    </Query>
                  )}
                </Quote>
              )}
            </DrawerSearch>
          )}
        </SearchWrapper>
        <MembershipDialog
          {...this.state.dialog}
          open={!!this.state.dialog}
          crossings
        />
      </AppWrapper>
    );
  }
}

export default compose(
  withData,
  withRouter,
  withApollo,
  withAppData,
  withCrossingsConfig,
  withReversePortal(MapCrossings),
  graphql(GET_QUOTE, {
    options: {
      fetchPolicy: FetchPolicy.CACHE_AND_NETWORK,
    },
    props: ({ data }) => ({
      quote: data?.quote,
    }),
    skip: ({ router }) => router.query?.bookingWidget === 'true',
  }),
  graphql(RESET_SESSION, {
    props: ({ mutate }) => ({
      resetSession: () => mutate({
        refetchQueries: ['Quote'],
      }),
    }),
  }),
)(Crossings);
